import * as api from "../../lib/api.js";
import * as store from "../../lib/store.js";
import type { StoredCredentials } from "./credentials";

/**
 * The device token's rotation, the client half of `POST /v1/devices/me/token` (#436).
 *
 * The server expires a device token a fixed `DEVICE_TOKEN_TTL_DAYS` (90) after it was
 * issued, and nothing but rotation extends it. Without this, every device was signed out
 * on day 90 and could only recover by re-registering -- a new device id, which is what
 * every membership, claim and audio row references, so rotation is the only way to keep
 * that history attached to the device.
 *
 * Mirrors `DEVICE_TOKEN_ROTATE_AFTER_DAYS` in `meetfolks/constants.py`: rotate once the
 * token is 30 days old, leaving 60 days of slack for boots whose rotation fails.
 */
export const TOKEN_ROTATE_AFTER_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * Whether the stored token is old enough to rotate.
 *
 * A credential with no `tokenIssuedAt` was written before this existed, so its age is
 * unknown and it rotates now -- it may already be close to day 90. An issue time in the
 * future (a clock that moved backwards) is not trusted either.
 */
export function tokenRotationDue(credentials: StoredCredentials, now: number): boolean {
  const issuedAt = credentials.tokenIssuedAt;
  if (typeof issuedAt !== "number" || !Number.isFinite(issuedAt) || issuedAt > now) return true;
  return now - issuedAt >= TOKEN_ROTATE_AFTER_MS;
}

export interface RotationDeps {
  rotate: () => Promise<{ token: string }>;
  save: (credentials: StoredCredentials) => Promise<void>;
  now: () => number;
}

const defaults: RotationDeps = {
  rotate: api.rotateDeviceToken,
  save: store.saveCredentials,
  now: Date.now,
};

/**
 * Rotate the token if it is due; returns the credentials to use from here on.
 *
 * A failed rotation changes nothing: the old token is still valid on the server, and
 * the next boot tries again. A successful one has already invalidated the old token, so
 * the new credentials are returned even if saving them fails -- this page keeps working
 * -- and the save is retried once, because a boot that reloads the old token is signed
 * out.
 */
export function rotateTokenIfDue(
  credentials: StoredCredentials,
  deps: RotationDeps = defaults
): Promise<StoredCredentials> {
  const run = rotate(credentials, deps);
  inFlight = run;
  return run;
}

/** The last rotation started, so a device reset can let it land first. */
let inFlight: Promise<unknown> = Promise.resolve();

/**
 * Resolves once any rotation already started has finished, successfully or not.
 *
 * Boot does not always wait for the rotation, so a reset of the device (#336) can start
 * while one is in flight. Wiping first would let the rotation's save land afterwards and
 * put the abandoned device's credentials back into the freshly wiped store.
 */
export function rotationSettled(): Promise<void> {
  return inFlight.then(
    () => undefined,
    () => undefined
  );
}

async function rotate(
  credentials: StoredCredentials,
  deps: RotationDeps
): Promise<StoredCredentials> {
  if (!tokenRotationDue(credentials, deps.now())) return credentials;

  let rotated: { token: string };
  try {
    rotated = await deps.rotate();
  } catch {
    return credentials;
  }
  if (typeof rotated?.token !== "string" || !rotated.token) return credentials;

  const next: StoredCredentials = { ...credentials, token: rotated.token, tokenIssuedAt: deps.now() };
  try {
    await deps.save(next);
  } catch {
    await deps.save(next).catch(() => {});
  }
  return next;
}

const CHANNEL = "meetfolks-device-token";

/**
 * Tell the app's other open tabs about a rotated token. Each tab holds the token in
 * memory, so without this a second tab -- possibly the one recording -- would keep
 * sending the token the server just invalidated and be treated as signed out.
 */
export function announceRotatedToken(credentials: StoredCredentials): void {
  if (typeof BroadcastChannel === "undefined") return;
  const channel = new BroadcastChannel(CHANNEL);
  channel.postMessage(credentials);
  channel.close();
}

/** Adopt tokens other tabs rotated. Only a credential for this same device is taken. */
export function listenForRotatedTokens(
  current: () => StoredCredentials | null,
  adopt: (credentials: StoredCredentials) => void
): void {
  if (typeof BroadcastChannel === "undefined") return;
  new BroadcastChannel(CHANNEL).onmessage = (event: MessageEvent) => {
    const incoming = event.data as StoredCredentials | null;
    const mine = current();
    if (
      mine &&
      typeof incoming?.token === "string" &&
      incoming.token &&
      incoming.claim_profile_id === mine.claim_profile_id
    ) {
      adopt(incoming);
    }
  };
}
